import { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { IEditorProps } from './Editor';
import { EditorConfigButton } from './EditorConfigButton';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { EditorConfigButtons } from './EditorConfigButtons';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { getRootFormDataDescriptions } from '@decisions/server/DecisionsFramework/Design/Form/FormDesignerService';
import { DataDescription } from '@decisions/server/DecisionsFramework/Design/Flow/Mapping/DataDescription';
import { EditorTextField } from './EditorTextField';

/**
 * Form Data Picker Editor.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export function FormDataPickerEditor({ child, path, onChange }: IEditorProps) {
  const context = useContext(PropertyEditorContext);
  const [formData, setFormData] = useState<DataDescription[]>([]);
  const showPick = useMemo(() => {
    return Array.isArray(formData) && !!formData.length;
  }, [formData]);

  useEffect(() => {
    const getData = async () => {
      try {
        const data = await getRootFormDataDescriptions({ formDesignerSessionInfoId: context.sessionId });
        setFormData(data);
      } catch (error) {
        console.error(error);
      }
    };
    getData();
  }, [context.sessionId]);

  const onValueChange = useCallback(
    (value: string) => {
      onChange(value, path);
      context.refreshContext();
    },
    [context, onChange, path]
  );

  const onShowPicker = useCallback(() => {
    context.onShowDialogue({
      node: child,
      path,
      editingContextId: context.editingContextId,
      name: 'ShowFormDataPicker',
      options: {
        rootData: formData,
        isInput: getEditorConfigValue(child, 'isReadOnly'),
        onPicked: (value: string) => {
          onValueChange(value);
        },
      },
    });
  }, [context, child, path, formData, onValueChange]);

  return (
    <EditorTextField {...{ child, path, onChange }} className="dpc-editor-fromdata-picker">
      {showPick && (
        <EditorConfigButtons>
          <EditorConfigButton onClick={onShowPicker}>Pick From Data</EditorConfigButton>
        </EditorConfigButtons>
      )}
    </EditorTextField>
  );
}
